/**
 * Form Builder V2 Main Component
 *
 * OpenForm-inspired form builder with:
 * - Device toggle (mobile/desktop)
 * - Drag-drop question reordering
 * - Live preview pane
 * - 13 question types
 * - Branching logic
 */

import { useEffect, useState } from 'react';
import {
  Save,
  Eye,
  EyeOff,
  Loader2,
  ExternalLink,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DeviceToggle } from './DeviceToggle';
import { QuestionList } from './QuestionList';
import { FormPreview } from './FormPreview';
import { TemplateSelector, TemplateBadge } from './TemplateSelector';
import { QuizSettingsCompact } from './QuizSettingsPanel';
import { NotificationSettingsCompact } from './NotificationSettingsPanel';
import { FormSettingsCard } from './FormSettingsCard';
import {
  useFormBuilder,
  FormConfig,
  FormQuestion,
  UseFormBuilderOptions,
} from './hooks/useFormBuilder';
import {
  FormTemplateType,
  isQuizTemplate,
  isOnboardingTemplate,
  needsRatingStep,
  defaultQuizConfig,
  defaultNotificationConfig,
  defaultReviewSettings,
} from '@/lib/forms/templates';

interface FormBuilderProps {
  formId?: string;
  businessId: string;
  initialForm?: FormConfig;
  initialQuestions?: FormQuestion[];
  onSave?: (form: FormConfig, questions: FormQuestion[]) => Promise<void>;
  onCreateFromTemplate?: (templateType: FormTemplateType) => Promise<{ formId: string } | null>;
  onBack?: () => void;
  previewUrl?: string;
  className?: string;
  showTemplateSelector?: boolean;  // Show template selector for new forms
}

export function FormBuilder({
  formId,
  businessId,
  initialForm,
  initialQuestions,
  onSave,
  onCreateFromTemplate,
  onBack,
  previewUrl,
  className,
  showTemplateSelector: initialShowTemplateSelector,
}: FormBuilderProps) {
  const [showPreview, setShowPreview] = useState(true);
  const [showTemplateSelector, setShowTemplateSelector] = useState(
    initialShowTemplateSelector ?? false
  );
  const [isCreatingForm, setIsCreatingForm] = useState(false);

  const {
    state,
    selectedQuestion,
    orderedQuestions,
    initializeForm,
    updateForm,
    updateFormUIText,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    toggleQuestionVisibility,
    setSelectedQuestionId,
    addQuestionOption,
    updateQuestionOption,
    deleteQuestionOption,
    addBranchingRule,
    updateBranchingRule,
    deleteBranchingRule,
    setDeviceMode,
    save,
    loadTemplate,
    updateQuizConfig,
    updateNotificationConfig,
    updateReviewSettings,
  } = useFormBuilder({
    initialForm,
    initialQuestions,
    onSave,
  });

  const { form, questions, deviceMode, isDirty, isSaving, selectedQuestionId } = state;

  // Initialize form on mount
  useEffect(() => {
    if (!form) {
      initializeForm(businessId, initialForm);
    }
  }, [businessId, initialForm, form, initializeForm]);

  if (!form) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const enabledQuestionIds =
    deviceMode === 'mobile'
      ? form.mobileConfig.enabledQuestions
      : form.desktopConfig.enabledQuestions;

  // Handle template selection and immediate form creation
  const handleTemplateSelectAndProceed = async (templateType: FormTemplateType) => {
    if (!onCreateFromTemplate) {
      // Fallback: just load template without saving (old behavior)
      loadTemplate(templateType);
      setShowTemplateSelector(false);
      return;
    }

    setIsCreatingForm(true);
    try {
      // Create form in database with selected template
      const result = await onCreateFromTemplate(templateType);
      if (result) {
        // Load the template into the builder state
        loadTemplate(templateType);
        setShowTemplateSelector(false);
      }
    } finally {
      setIsCreatingForm(false);
    }
  };


  // Show template selector
  if (showTemplateSelector) {
    return (
      <TooltipProvider>
        <div className={cn('flex flex-col h-full', className)}>
          <TemplateSelector
            onSelectAndProceed={handleTemplateSelectAndProceed}
            onBack={onBack}
            isLoading={isCreatingForm}
          />
        </div>
      </TooltipProvider>
    );
  }

  // Check if form is quiz, onboarding, or review type
  const isQuiz = form.templateType ? isQuizTemplate(form.templateType) : false;
  const isOnboarding = form.templateType ? isOnboardingTemplate(form.templateType) : false;
  const isReviewType = form.templateType ? needsRatingStep(form.templateType) : true; // Default to true for custom forms

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col h-full', className)}>
        {/* Compact Header Toolbar with integrated Step Indicator */}
        <Card className="flex items-center justify-between gap-4 p-4">
          {/* Left: Back + Title + Unsaved indicator */}
          <div className="flex items-center gap-3 min-w-0">
            {onBack && (
              <Button variant="outline" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <Input
              value={form.title}
              onChange={(e) => updateForm({ title: e.target.value })}
              className="text-base font-semibold border-none shadow-none focus-visible:ring-0 px-0 w-auto max-w-[200px]"
              placeholder="Form Title"
            />
            {form.templateType && (
              <TemplateBadge templateType={form.templateType} />
            )}
            {isDirty && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">Unsaved</span>
            )}
          </div>

          {/* Center: Step Indicator */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!selectedQuestionId) {
                  if (orderedQuestions.length > 0) {
                    setSelectedQuestionId(orderedQuestions[0].id);
                  }
                } else {
                  const currentIndex = orderedQuestions.findIndex(q => q.id === selectedQuestionId);
                  if (currentIndex > 0) {
                    setSelectedQuestionId(orderedQuestions[currentIndex - 1].id);
                  }
                }
              }}
              disabled={!selectedQuestionId || orderedQuestions.findIndex(q => q.id === selectedQuestionId) === 0}
              className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground font-medium min-w-[90px] text-center">
              {orderedQuestions.length === 0
                ? 'No questions'
                : selectedQuestionId
                  ? `Question ${orderedQuestions.findIndex(q => q.id === selectedQuestionId) + 1} / ${orderedQuestions.length}`
                  : `${orderedQuestions.length} question${orderedQuestions.length !== 1 ? 's' : ''}`
              }
            </span>
            <button
              onClick={() => {
                if (!selectedQuestionId) {
                  if (orderedQuestions.length > 0) {
                    setSelectedQuestionId(orderedQuestions[0].id);
                  }
                } else {
                  const currentIndex = orderedQuestions.findIndex(q => q.id === selectedQuestionId);
                  if (currentIndex < orderedQuestions.length - 1) {
                    setSelectedQuestionId(orderedQuestions[currentIndex + 1].id);
                  }
                }
              }}
              disabled={!selectedQuestionId || orderedQuestions.findIndex(q => q.id === selectedQuestionId) === orderedQuestions.length - 1}
              className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1">
            <DeviceToggle
              deviceMode={deviceMode}
              onDeviceModeChange={setDeviceMode}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showPreview ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {showPreview ? 'Hide preview' : 'Show preview'}
              </TooltipContent>
            </Tooltip>
            {previewUrl && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" asChild>
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open live preview</TooltipContent>
              </Tooltip>
            )}
            <Button
              onClick={save}
              disabled={!isDirty || isSaving}
              size="sm"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
          </div>
        </Card>

        {/* Main Content Area - scrollable container */}
        <div className="flex-1 overflow-y-auto">
          {/* Questions + Preview Row */}
          <div className="flex gap-4 min-h-[400px]">
            {/* Question List Panel */}
            <div
              className={cn(
                showPreview ? 'w-[400px] shrink-0' : 'flex-1 max-w-3xl mx-auto'
              )}
            >
              <div className="py-4 space-y-4">
                <QuestionList
                  questions={orderedQuestions}
                  allQuestions={questions}
                  selectedQuestionId={selectedQuestionId}
                  deviceMode={deviceMode}
                  enabledQuestionIds={enabledQuestionIds}
                  onSelect={setSelectedQuestionId}
                  onReorder={reorderQuestions}
                  onUpdate={updateQuestion}
                  onDelete={deleteQuestion}
                  onDuplicate={duplicateQuestion}
                  onToggleVisibility={toggleQuestionVisibility}
                  onAddQuestion={addQuestion}
                  onAddOption={addQuestionOption}
                  onUpdateOption={updateQuestionOption}
                  onDeleteOption={deleteQuestionOption}
                  onAddBranchingRule={addBranchingRule}
                  onUpdateBranchingRule={updateBranchingRule}
                  onDeleteBranchingRule={deleteBranchingRule}
                  isQuizMode={isQuiz}
                />

                {/* Quiz Settings Panel (inline with questions for quiz mode) */}
                {isQuiz && (
                  <QuizSettingsCompact
                    config={form.quizConfig || defaultQuizConfig}
                    onChange={updateQuizConfig}
                  />
                )}

                {/* Notification Settings Panel (inline for onboarding) */}
                {isOnboarding && (
                  <NotificationSettingsCompact
                    config={form.notificationConfig || defaultNotificationConfig}
                    onChange={updateNotificationConfig}
                  />
                )}
              </div>
            </div>

            {/* Preview Panel */}
            {showPreview && (
              <div className="flex-1 min-w-0">
                <FormPreview
                  form={form}
                  questions={orderedQuestions}
                  deviceMode={deviceMode}
                  onDeviceModeChange={setDeviceMode}
                  editingEnabled={true}
                  onUpdateQuestion={updateQuestion}
                  onUpdateUIText={updateFormUIText}
                />
              </div>
            )}
          </div>

          {/* Settings Section - always visible below columns */}
          <div className="py-4">
            <FormSettingsCard
              form={form}
              businessId={businessId}
              onUpdate={updateForm}
              onUpdateUIText={updateFormUIText}
              reviewSettings={form.reviewSettings || defaultReviewSettings}
              onUpdateReviewSettings={updateReviewSettings}
              showReviewSettings={isReviewType && !isQuiz}
            />
          </div>
        </div>

      </div>
    </TooltipProvider>
  );
}
